import LgpdNotice from '@/components/feature/LgpdNotice';
import { useEffect, useState } from 'react';
import { depoimentosPalestra } from '../../mocks/depoimentos';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingCTA from '@/components/feature/FloatingCTA';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import AnimatedStatCard from '@/components/feature/AnimatedStatCard';

declare const gtag: (command: string, action: string, params?: Record<string, unknown>) => void;

// ─── Quote Form ──────────────────────────────────────────────────────────────

function QuoteForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    empresa: '',
    eventType: '',
    participants: '',
    date: '',
    local: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus('idle');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));

      const res = await fetch('https://ordem-de-servico.stibpalestrasilenciosasp.com.br/pedido.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          empresa: '',
          eventType: '',
          participants: '',
          date: '',
          local: '',
          message: '',
        });
        if (typeof gtag !== 'undefined') {
          gtag('event', 'generate_lead', { event_category: 'Form', event_label: 'Rio Quote' });
        }
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setStatus('idle'), 8000);
    }
  };

  const inputClass =
    'w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none font-body text-sm text-gray-800 bg-white transition-colors';
  const labelClass = 'block font-body text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      id="rio-quote-form"
      aria-label="Formulário de orçamento palestra silenciosa Rio de Janeiro"
      className="space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="rj-name" className={labelClass}>Nome Completo *</label>
          <input
            type="text"
            id="rj-name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Seu nome"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="rj-email" className={labelClass}>E-mail *</label>
          <input
            type="email"
            id="rj-email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="seu@email.com.br"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="rj-phone" className={labelClass}>Telefone / WhatsApp *</label>
          <input
            type="tel"
            id="rj-phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            placeholder="(21) 9 9999-9999"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="rj-empresa" className={labelClass}>Empresa / Organização</label>
          <input
            type="text"
            id="rj-empresa"
            name="empresa"
            value={formData.empresa}
            onChange={handleChange}
            placeholder="Nome da empresa ou entidade"
            className={inputClass}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="rj-eventType" className={labelClass}>Tipo de Evento *</label>
          <select
            id="rj-eventType"
            name="eventType"
            required
            value={formData.eventType}
            onChange={handleChange}
            className={inputClass}
          >
            <option value="">Selecione...</option>
            <option value="Congresso / Conferência">Congresso / Conferência</option>
            <option value="Evento Corporativo">Evento Corporativo</option>
            <option value="Summit / Fórum">Summit / Fórum</option>
            <option value="Festival / Show Cultural">Festival / Show Cultural</option>
            <option value="Evento Esportivo">Evento Esportivo</option>
            <option value="Treinamento Corporativo">Treinamento Corporativo</option>
            <option value="Evento Multilíngue">Evento Multilíngue</option>
            <option value="Workshop / Capacitação">Workshop / Capacitação</option>
            <option value="Outro">Outro</option>
          </select>
        </div>
        <div>
          <label htmlFor="rj-participants" className={labelClass}>Número de Participantes</label>
          <select
            id="rj-participants"
            name="participants"
            value={formData.participants}
            onChange={handleChange}
            className={inputClass}
          >
            <option value="">Selecione...</option>
            <option value="Até 50">Até 50</option>
            <option value="50 a 150">50 a 150</option>
            <option value="150 a 300">150 a 300</option>
            <option value="300 a 500">300 a 500</option>
            <option value="Acima de 500">Acima de 500</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="rj-date" className={labelClass}>Data do Evento</label>
          <input
            type="date"
            id="rj-date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="rj-local" className={labelClass}>Local / Venue</label>
          <input
            type="text"
            id="rj-local"
            name="local"
            value={formData.local}
            onChange={handleChange}
            placeholder="Ex.: Riocentro, Windsor, Hotel..."
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor="rj-message" className={labelClass}>Detalhes do Evento</label>
        <textarea
          id="rj-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          maxLength={500}
          rows={4}
          placeholder="Conte sobre o evento, número de idiomas, necessidades especiais..."
          className={`${inputClass} resize-none`}
        />
        <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500</p>
      </div>

      <LgpdNotice kind="orcamento" className="mb-4 col-span-full" />
            <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-primary text-white py-4 rounded-full font-body font-bold text-base uppercase tracking-wide hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'ENVIANDO...' : 'SOLICITAR ORÇAMENTO GRATUITO'}
      </button>

      {status === 'success' && (
        <p className="text-center text-green-600 font-body text-sm font-semibold" role="status">
          Orçamento enviado! Nossa equipe retorna em até 24 horas.
        </p>
      )}
      {status === 'error' && (
        <p className="text-center text-red-500 font-body text-sm" role="alert">
          Erro ao enviar. Por favor, tente novamente ou ligue: (11) 91777-6227
        </p>
      )}
    </form>
  );
}

// ─── FAQ Item ────────────────────────────────────────────────────────────────

function FAQItem({ question, answer, index }: { question: string; answer: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-gray-100 rounded-2xl overflow-hidden"
      itemScope
      itemType="https://schema.org/Question"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-7 py-5 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-left"
        aria-expanded={open}
        id={`faq-rj-${index}`}
      >
        <h4
          className="font-body font-semibold text-secondary text-base leading-snug pr-4"
          itemProp="name"
        >
          {question}
        </h4>
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-primary/10">
          <i className={`ri-${open ? 'subtract' : 'add'}-line text-primary text-base`}></i>
        </div>
      </button>
      {open && (
        <div
          className="px-7 pb-6 bg-white"
          itemScope
          itemType="https://schema.org/Answer"
          itemProp="acceptedAnswer"
        >
          <p className="font-body text-gray-600 text-sm leading-relaxed" itemProp="text">
            {answer}
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────

const TESTIMONIALS = depoimentosPalestra.slice(0, 3).map((d) => ({
  name: d.nome,
  text: d.texto,
  context: d.contexto,
  rating: 5,
}));

const FAQS = [
  {
    question: 'A STIB tem equipamentos e equipe técnica no Rio de Janeiro?',
    answer: 'Sim. A STIB conta com equipamentos e profissionais presentes no Rio de Janeiro, o que facilita a montagem, o suporte técnico presencial e a resposta rápida para eventos na capital fluminense e na região metropolitana. Não possuímos escritório físico no Rio: o atendimento é feito por equipe e equipamentos em campo, com orçamento centralizado pelo WhatsApp (11) 91777-6227.',
  },
  {
    question: 'A palestra silenciosa funciona em eventos de grande porte no Riocentro?',
    answer:
      'Sim, com total competência. O sistema cobre grandes pavilhões como os do Riocentro sem perda de sinal. Já operamos em eventos com mais de 1.000 participantes no Pavilhão 3 e 4. Realizamos um estudo de campo prévio no espaço para garantir cobertura de sinal em 100% da área.',
  },
  {
    question: 'Quantos canais simultâneos são suportados no Rio de Janeiro?',
    answer:
      'Nosso sistema suporta até 10 canais de transmissão simultâneos — ideal para congressos bilíngues (PT/EN), eventos culturais multilíngues ou múltiplas trilhas de conteúdo dentro do mesmo espaço. Cada participante escolhe o canal no próprio receptor de bolso.',
  },
  {
    question: 'Vocês atendem em toda a cidade do Rio ou apenas na Zona Sul?',
    answer:
      'Atendemos toda a cidade do Rio de Janeiro: Zona Sul (Ipanema, Leblon, Botafogo, Flamengo), Centro (Centro de Convenções SulAmérica, Marina da Glória), Barra da Tijuca (Riocentro, Windsor Barra), Recreio, Norte (Expo Mag) e região metropolitana (Niterói, São Gonçalo).',
  },
  {
    question: 'Qual a diferença entre palestra silenciosa e sistema de tradução simultânea tradicional?',
    answer:
      'O sistema de palestra silenciosa usa transmissão FM/RF sem fio e receptores individuais leves (como fones de ouvido), eliminando a necessidade de cabines físicas de interpretação ou instalações pesadas. A economia em montagem é de 50 a 70%, e o resultado sonoro é profissional e privado — apenas os participantes com fone ouvem o conteúdo.',
  },
  {
    question: 'Qual o prazo mínimo para contratar o serviço no Rio de Janeiro?',
    answer:
      'Recomendamos uma antecedência mínima de 5 dias úteis para garantir logística, setup e testes. Para eventos de grande porte (acima de 300 pessoas) ou com múltiplos idiomas, o ideal é de 10 dias. Solicitações urgentes são avaliadas caso a caso — entre em contato pelo WhatsApp (21).',
  },
  {
    question: 'Como funciona a logística de montagem em eventos no Rio de Janeiro?',
    answer:
      'Nossa equipe chega com 2h de antecedência para setup completo e testes de sinal em toda a área do evento. Atendemos Zona Sul, Barra, Centro e Região Metropolitana. Para o Riocentro e grandes pavilhões, utilizamos múltiplos pontos de transmissão para garantir cobertura total. Suporte técnico presente do início ao fim.',
  },
];

const VENUES = [
  { name: 'Riocentro — Pavilhões', icon: 'ri-building-4-line', capacity: '10.000+' },
  { name: 'C.C. SulAmérica', icon: 'ri-building-line', capacity: '3.000' },
  { name: 'Windsor Oceânico (Ipanema)', icon: 'ri-hotel-line', capacity: '800' },
  { name: 'Marina da Glória', icon: 'ri-anchor-line', capacity: '1.500' },
  { name: 'Museu do Amanhã', icon: 'ri-lightbulb-flash-line', capacity: '500' },
  { name: 'Copacabana Palace', icon: 'ri-hotel-line', capacity: '400' },
  { name: 'Expo Mag', icon: 'ri-store-3-line', capacity: '2.000' },
  { name: 'Hotel Nacional (São Conrado)', icon: 'ri-building-2-line', capacity: '600' },
];

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function RioLandingPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: benefitsRef, isVisible: benefitsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: venuesRef, isVisible: venuesVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: testimonialsRef, isVisible: testimonialsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.1 });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

  useEffect(() => {
    document.title =
      'Palestra Silenciosa Rio de Janeiro | Congressos, Eventos e Shows no RJ | STIB';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Palestra silenciosa Rio de Janeiro: áudio sem fio para congressos, eventos corporativos, festivais e shows no RJ. Equipamentos e profissionais presentes no Rio, técnico no local, orçamento em 24h!'
      );
    }

    const geoPos = document.querySelector('meta[name="geo.position"]');
    if (geoPos) geoPos.setAttribute('content', '-22.9068;-43.1729');
    const geoReg = document.querySelector('meta[name="geo.region"]');
    if (geoReg) geoReg.setAttribute('content', 'BR-RJ');
    const geoPlace = document.querySelector('meta[name="geo.placename"]');
    if (geoPlace) geoPlace.setAttribute('content', 'Rio de Janeiro');

    const schemas = [
      {
        id: 'rj-localbusiness',
        data: {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: 'STIB Palestra Silenciosa Rio de Janeiro',
          description:
            'Aluguel de fones sem fio e sistemas de palestra silenciosa para eventos corporativos, congressos, festivais e shows no Rio de Janeiro — RJ. Equipamentos e profissionais presentes na cidade.',
          url: `${siteUrl}/rio-de-janeiro`,
          telephone: '+55-11-91777-6227',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'São Paulo',
            addressRegion: 'SP',
            addressCountry: 'BR',
          },
          geo: { '@type': 'GeoCoordinates', latitude: -23.5505, longitude: -46.6333 },
          areaServed: { '@type': 'City', name: 'Rio de Janeiro' },
        },
      },
      {
        id: 'rj-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Palestra Silenciosa Rio de Janeiro',
              item: `${siteUrl}/rio-de-janeiro`,
            },
          ],
        },
      },
      {
        id: 'rj-faq',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
          })),
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [siteUrl]);

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
        aria-label="Palestra Silenciosa Rio de Janeiro — Hero"
      >
        <div className="absolute inset-0">
          <img
            src="/img/stib/cidade-rio.webp"
            alt="Palestra Silenciosa Rio de Janeiro — Eventos e congressos no RJ"
            title="Palestra Silenciosa Rio de Janeiro — STIB"
            className={`w-full h-full object-cover object-top transition-all duration-1200 ${heroVisible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/40 to-black/70"></div>
        </div>

        <div className="absolute top-24 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6">
            <Breadcrumb
              variant="light"
              items={[
                { label: 'Home', to: '/' },
                { label: 'Cidades', to: '/cidades' },
                { label: 'Palestra Silenciosa Rio de Janeiro' },
              ]}
            />
          </div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
          <div
            className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-widest px-5 py-2 rounded-full mb-8 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-primary text-sm"></i>
            </div>
            Rio de Janeiro — RJ
          </div>

          <header>
            <h1
              className={`font-heading font-extrabold text-6xl md:text-8xl text-white mb-6 tracking-tight leading-none lowercase transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              palestra silenciosa<br />
              <span className="text-primary">rio de janeiro</span>
            </h1>
          </header>

          <p
            className={`font-body font-light text-xl md:text-2xl text-white/85 mb-10 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            Tecnologia de áudio sem fio para congressos, eventos corporativos, festivais e shows
            no Rio de Janeiro, com equipamentos e profissionais presentes na cidade. Múltiplos canais simultâneos, técnico no local.
          </p>

          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <a
              href="/orcamento"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SOLICITAR ORÇAMENTO
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-arrow-down-line text-white text-lg"></i>
              </div>
            </a>
            <a
              href="https://wa.me/5521999990000?text=Olá! Preciso de orçamento para palestra silenciosa no Rio de Janeiro."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-full font-body font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-green-400 text-xl"></i>
              </div>
              WhatsApp RJ
            </a>
          </div>

          {/* Trust badges */}
          <div
            className={`flex flex-wrap items-center justify-center gap-6 mt-14 transition-all duration-700 delay-400 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            {[
              { icon: 'ri-building-4-line', label: 'Riocentro & SulAmérica' },
              { icon: 'ri-headphone-line', label: 'Até 10 canais simultâneos' },
              { icon: 'ri-timer-line', label: 'Resposta em 24 horas' },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-2 text-white/80 text-sm font-body">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${b.icon} text-primary text-base`}></i>
                </div>
                {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────────── */}
      <section ref={statsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { numericValue: 50, suffix: '+', label: 'Empresas e Entidades', icon: 'ri-briefcase-line' },
              { numericValue: 3, suffix: '', label: 'Canais Simultâneos', icon: 'ri-headphone-line' },
              { numericValue: 99, suffix: '%', label: 'Satisfação dos Clientes', icon: 'ri-star-line' },
            ].map((s, i) => (
              <AnimatedStatCard
                key={i}
                numericValue={s.numericValue}
                suffix={s.suffix}
                label={s.label}
                icon={s.icon}
                delay={i * 100}
                visible={statsVisible}
                numberColor="text-secondary"
                className="p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5"
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── FORM + INFO ──────────────────────────────────────── */}
      <section
        id="orcamento"
        ref={formRef}
        className="py-24 bg-gradient-to-br from-gray-50 to-white"
        aria-label="Formulário de orçamento palestra silenciosa Rio de Janeiro"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Info lateral */}
            <div
              className={`transition-all duration-700 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
            >
              <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
                Orçamento Gratuito · Retorno em 24 Horas
              </span>
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-secondary mb-6 lowercase leading-tight">
                quanto custa palestra silenciosa no rio de janeiro?
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-10">
                Preencha o formulário ao lado e receba uma proposta comercial detalhada, adequada
                ao porte do seu evento, ao venue e ao número de canais necessários.
              </p>

              <div className="space-y-6 mb-10">
                {[
                  {
                    icon: 'ri-building-4-line',
                    title: 'Cobertura Total no Riocentro',
                    desc: 'Já operamos em todos os pavilhões do Riocentro. Conhecemos a acústica e a infraestrutura de cada espaço — setup rápido e sem surpresas.',
                  },
                  {
                    icon: 'ri-music-2-line',
                    title: 'Shows, Festivais e Eventos Culturais',
                    desc: 'Além de congressos, o sistema é ideal para tours guiados em museus, festivais bilíngues, eventos da indústria do entretenimento e produções audiovisuais no RJ.',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: 'Equipamentos Certificados',
                    desc: 'Fones e transmissores com certificação Anatel, sinal RF estável mesmo em ambientes com muito público e estruturas metálicas pesadas.',
                  },
                  {
                    icon: 'ri-tools-line',
                    title: 'Técnico Dedicado no Local',
                    desc: 'Nossa equipe chega com 2h de antecedência para setup e testes. Suporte em tempo real do início ao fim do evento no Rio de Janeiro.',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                      <i className={`${item.icon} text-primary text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-secondary text-base mb-1">{item.title}</h3>
                      <p className="font-body text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-secondary/5 border border-secondary/10 rounded-2xl p-6">
                <p className="font-body text-sm text-gray-600 leading-relaxed">
                  <strong className="text-secondary">Atendimento no Rio de Janeiro — 2026:</strong><br />
                  Cobertura em toda a cidade: Zona Sul, Barra e Centro<br />
                  Fones com cancelamento de ruído para ambientes externos<br />
                  Técnico presencial incluso em todos os pacotes
                </p>
              </div>
            </div>

            {/* Formulário */}
            <div
              className={`bg-white rounded-3xl p-10 border border-gray-100 transition-all duration-700 delay-200 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary">
                  <i className="ri-send-plane-line text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-secondary">Solicite seu Orçamento</h3>
                  <p className="font-body text-xs text-gray-400">Gratuito · Sem compromisso · Resposta em 24h</p>
                </div>
              </div>
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── DIFERENCIAIS RJ ─────────────────────────────────── */}
      <section ref={benefitsRef} className="py-24 bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Por que escolher a STIB no Rio
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl lowercase transition-all duration-700 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              feito para o mercado carioca
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-building-4-line',
                title: 'Especialistas nos Venues do RJ',
                desc: 'Operação recorrente no Riocentro, SulAmérica, Windsor, Marina da Glória e Copacabana Palace — sabemos de cor a acústica e a logística de cada espaço.',
              },
              {
                icon: 'ri-music-fill',
                title: 'Shows, Festivais e Entretenimento',
                desc: 'O Rio tem uma das cenas culturais mais ricas do Brasil. Oferecemos solução para eventos artísticos, experiências imersivas e festivais multilíngues com público diverso.',
              },
              {
                icon: 'ri-group-line',
                title: 'Eventos de Grande Escala',
                desc: 'Capacidade de atender eventos com mais de 1.500 participantes simultâneos. Logística especializada para grandes pavilhões de feira e convenção.',
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Cobertura em Toda a Cidade',
                desc: 'Da Zona Sul à Barra, do Centro ao Norte — atendemos qualquer bairro e município da região metropolitana, incluindo Niterói e São Gonçalo.',
              },
              {
                icon: 'ri-headphone-line',
                title: 'Qualidade de Áudio Premium',
                desc: 'Fones de ouvido de alta fidelidade com cancelamento de ruído. Ideal para ambientes ruidosos como feiras, pavilhões e locais ao ar livre.',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Suporte Técnico Presencial',
                desc: 'Técnico dedicado presente em todo o evento. Chegamos antes, ficamos durante e saímos depois — sua operação está coberta do início ao fim.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/20 mb-6">
                  <i className={`${item.icon} text-primary text-2xl`}></i>
                </div>
                <h3 className="font-heading font-bold text-white text-lg mb-3">{item.title}</h3>
                <p className="font-body text-white/70 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VENUES ──────────────────────────────────────────── */}
      <section ref={venuesRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div
              className={`transition-all duration-700 ${venuesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
            >
              <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
                Onde já atuamos
              </span>
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-secondary mb-6 lowercase leading-tight">
                principais venues no rio
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-10">
                Do Riocentro ao Copacabana Palace, a <strong>palestra silenciosa no Rio de Janeiro</strong> já
                aconteceu nos maiores e mais exigentes espaços da cidade. Conheça onde já operamos:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {VENUES.map((v, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-4 p-4 rounded-xl bg-gray-50 transition-all duration-500 ${venuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{ transitionDelay: `${i * 60}ms` }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                      <i className={`${v.icon} text-primary text-base`}></i>
                    </div>
                    <div>
                      <p className="font-body font-semibold text-secondary text-sm">{v.name}</p>
                      <p className="font-body text-xs text-gray-400">até {v.capacity} pessoas</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div
              className={`relative h-[520px] rounded-3xl overflow-hidden transition-all duration-700 delay-200 ${venuesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
            >
              <img
                src="/img/stib/cidade-rio.webp"
                alt="Centro de Convenções no Rio de Janeiro — Palestra Silenciosa RJ"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <p className="font-body text-white/80 text-sm mb-2">Atendimento completo em</p>
                <h3 className="font-heading font-bold text-white text-2xl">Rio de Janeiro e Região Metropolitana</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DEPOIMENTOS ──────────────────────────────────────── */}
      <section
        ref={testimonialsRef}
        className="py-24 bg-gradient-to-br from-gray-50 to-white"
        aria-label="Depoimentos de clientes Rio de Janeiro"
        itemScope
        itemType="https://schema.org/ItemList"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Quem já contratou no RJ
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              o que dizem nossos clientes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-8 border border-gray-100 transition-all duration-700 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className="flex items-center gap-1 mb-5">
                  {[...Array(t.rating)].map((_, j) => (
                    <i key={j} className="ri-star-fill text-yellow-400 text-base"></i>
                  ))}
                </div>
                <p
                  className="font-body text-gray-600 text-sm leading-relaxed mb-6 italic"
                 
                >
                  "{t.text}"
                </p>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary/10 flex-shrink-0">
                    <span className="font-heading font-bold text-secondary text-lg">
                      {t.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-heading font-bold text-secondary text-sm">
                      {t.name}
                    </p>
                    {t.context && (
                      <p className="font-body text-xs text-gray-500">{t.context}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────── */}
      <section
        ref={faqRef}
        className="py-24 bg-white"
        aria-label="Perguntas frequentes palestra silenciosa Rio de Janeiro"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Dúvidas Frequentes
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              faq — rio de janeiro
            </h2>
          </div>

          <div
            className={`space-y-4 transition-all duration-700 delay-200 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {FAQS.map((f, i) => (
              <FAQItem key={i} question={f.question} answer={f.answer} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ───────────────────────────────────────── */}
      <section className="relative py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/img/stib/fone-gala-noite.webp"
            alt="Solicite orçamento palestra silenciosa Rio de Janeiro"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary/85 via-secondary/70 to-secondary/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className="max-w-2xl">
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-white lowercase leading-tight mb-6">
                pronto para transformar<br />
                seu evento no rio?
              </h2>
              <p className="font-body text-lg text-white/80 leading-relaxed">
                <strong className="text-white">Agenda com vagas limitadas</strong> para eventos no Rio de Janeiro.
                Confirme sua data e receba orçamento completo em até 24 horas.
              </p>
            </div>

            <div className="flex flex-col gap-4 flex-shrink-0">
              <a
                href="/orcamento"
                className="inline-flex items-center justify-center gap-3 bg-primary text-white px-12 py-5 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                VER FORMULÁRIO DE ORÇAMENTO
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-arrow-up-line text-white text-lg"></i>
                </div>
              </a>
              <a
                href="https://wa.me/5521999990000?text=Olá! Preciso de orçamento para palestra silenciosa no Rio de Janeiro."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-white text-secondary px-12 py-5 rounded-full font-body font-bold text-base uppercase hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-green-500 text-xl"></i>
                </div>
                FALAR NO WHATSAPP
              </a>
              <Link
                to="/blog/palestra-silenciosa-rio-de-janeiro"
                className="text-center font-body text-sm text-white/70 hover:text-white underline underline-offset-4 transition-colors cursor-pointer"
              >
                Ler nosso guia completo sobre Palestra Silenciosa Rio de Janeiro &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}